import { type Theme } from '@mui/material/styles';
import {
  CalendarCheckIcon,
  CircleCheckBigIcon,
  EllipsisIcon,
  NotebookPenIcon,
  PhoneIcon,
  TrophyIcon,
  XIcon,
} from 'lucide-react';

import { type ActivityNextStepAction } from '@modules/_shared/components/feed/ActivityNextStepsDialog';
import type { Translate } from '@shared/hooks/locale';

export type OutcomeFollowUpAction = Extract<
  ActivityNextStepAction,
  | 'schedule_visit'
  | 'schedule_call'
  | 'schedule_task'
  | 'lead_lost'
  | 'lead_won'
  | 'note'
  | 'do_nothing'
>;

export type ActivityFollowUpContext<Task> = {
  task: Task;
  success: boolean;
  isBrokeragePipeline: boolean;
};

type CreateNextStepOptionsArgs = {
  t: Translate;
  palette: Theme['palette'];
  canMarkLeadLost: boolean;
  followUpSuccess: boolean;
  followUpBrokerage: boolean;
  parentType?: null | 'Enquiry' | 'Lot' | 'Lead' | 'User' | 'BuyerLead';
};

const createScheduleVisitOption = <Action extends OutcomeFollowUpAction>(
  t: Translate,
  color: string,
) => ({
  key: 'schedule_visit' as Action,
  label: t('Schedule a visit'),
  icon: <CalendarCheckIcon size={18} />,
  avatarColor: color,
});

const createScheduleCallOption = <Action extends OutcomeFollowUpAction>(
  t: Translate,
  color: string,
) => ({
  key: 'schedule_call' as Action,
  label: t('Schedule a call'),
  icon: <PhoneIcon size={18} />,
  avatarColor: color,
});

const createScheduleTaskOption = <Action extends OutcomeFollowUpAction>(
  t: Translate,
  color: string,
) => ({
  key: 'schedule_task' as Action,
  label: t('Schedule a task'),
  icon: <CircleCheckBigIcon size={18} />,
  avatarColor: color,
});

const createNoteOption = <Action extends OutcomeFollowUpAction>(
  t: Translate,
  color: string,
) => ({
  key: 'note' as Action,
  label: t('Write a note'),
  icon: <NotebookPenIcon size={18} />,
  avatarColor: color,
});

const createLeadWonOption = <Action extends OutcomeFollowUpAction>(
  label: string,
  color: string,
) => ({
  key: 'lead_won' as Action,
  label,
  icon: <TrophyIcon size={18} />,
  avatarColor: color,
});

const createLeadLostOption = <Action extends OutcomeFollowUpAction>(
  t: Translate,
  color: string,
) => ({
  key: 'lead_lost' as Action,
  label: t('Lead lost'),
  icon: <XIcon size={18} />,
  avatarColor: color,
});

const createDoNothingOption = <Action extends OutcomeFollowUpAction>(
  t: Translate,
  color: string,
) => ({
  key: 'do_nothing' as Action,
  label: t('Do nothing'),
  icon: <EllipsisIcon size={18} />,
  avatarColor: color,
});

const createCoreSchedulingOptions = (t: Translate, primaryColor: string) => [
  createScheduleVisitOption(t, primaryColor),
  createScheduleCallOption(t, primaryColor),
  createScheduleTaskOption(t, primaryColor),
];

export const createActivityNextStepOptions = ({
  t,
  palette,
  canMarkLeadLost,
  followUpSuccess,
  followUpBrokerage,
  parentType,
}: CreateNextStepOptionsArgs) => {
  const primaryColor = palette.primary.main;
  const errorColor = palette.error.main;
  const successColor = palette.success.main;
  const greyColor = palette.grey[500];

  // Lot parent type: visit, call, task, note only
  if (parentType === 'Lot') {
    return [
      ...createCoreSchedulingOptions(t, primaryColor),
      createNoteOption(t, primaryColor),
      createDoNothingOption(t, greyColor),
    ];
  }

  // BuyerLead parent type: visit, call, task, note, optional lead_won, lead_lost, do_nothing
  if (parentType === 'BuyerLead') {
    const options = [
      ...createCoreSchedulingOptions(t, primaryColor),
      createNoteOption(t, primaryColor),
    ];

    if (followUpSuccess) {
      options.push(createLeadWonOption(t('Lead won'), successColor));
    }

    options.push(
      createLeadLostOption(t, errorColor),
      createDoNothingOption(t, greyColor),
    );

    return options;
  }

  // Default behavior for other parent types
  const options = [...createCoreSchedulingOptions(t, primaryColor)];

  if (canMarkLeadLost) {
    options.push(createLeadLostOption(t, errorColor));
  }

  if (followUpSuccess && followUpBrokerage) {
    options.push(createLeadWonOption(t('Mandate won'), successColor));
  }

  options.push(createDoNothingOption(t, greyColor));

  return options;
};
